import { useEffect, useRef } from 'react';
import type { GameServices } from '@/app/services';
import type { Game } from '@/game/Game';
import { detectWebGL } from '@/game/engine/resolution';
import { useI18n } from '@/ui/i18n/I18nProvider';
import styles from './GameCanvas.module.css';

export type GameStatus = 'loading' | 'ready' | 'unsupported' | 'error';

/**
 * The game (Pixi and everything drawn with it) is its own chunk, about a third of the app: the shell and
 * the loading screen start without it. AppShell calls this while the save boots so both load together.
 */
export const loadGame = () => import('@/game/Game');

interface Props {
  services: GameServices;
  onStatus: (status: GameStatus, error?: unknown) => void;
  onGame: (game: Game | null) => void;
  onContextLost: (lost: boolean) => void;
  /** Loading progress 0..1 for S00. */
  onProgress?: (fraction: number) => void;
}

/** Mounts the single Pixi game into a full-bleed layer. Safe under React StrictMode double-mount. */
export function GameCanvas({ services, onStatus, onGame, onContextLost, onProgress }: Props) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const callbacks = useRef({ onStatus, onGame, onContextLost, onProgress });
  useEffect(() => {
    callbacks.current = { onStatus, onGame, onContextLost, onProgress };
  });

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    if (!detectWebGL()) {
      callbacks.current.onStatus('unsupported');
      return;
    }
    let cancelled = false;
    let game: Game | null = null;
    loadGame()
      .then(({ Game }) =>
        Game.create({
          container,
          services,
          onContextLost: () => {
            callbacks.current.onContextLost(true);
          },
          onContextRestored: () => {
            callbacks.current.onContextLost(false);
          },
          onProgress: (f) => {
            callbacks.current.onProgress?.(f);
          },
        }),
      )
      .then(
        (created) => {
          if (cancelled) {
            created.destroy();
            return;
          }
          game = created;
          callbacks.current.onGame(created);
          callbacks.current.onStatus('ready');
        },
        (error: unknown) => {
          if (!cancelled) callbacks.current.onStatus('error', error);
        },
      );
    return () => {
      cancelled = true;
      game?.destroy();
      callbacks.current.onGame(null);
    };
  }, [services]);

  // ux-screens §6.4: the room is one interactive surface for assistive tech; shortcuts are listed in S26.
  return (
    <div
      ref={ref}
      className={styles.layer}
      data-testid="game-canvas"
      role="application"
      aria-label={t('ui.canvas.label')}
    />
  );
}
